import React from 'react';
import debug from 'debug';

import { Navigate, useParams } from 'react-router';
import { getMarketplace, getMetadataService } from '../../services/ServiceFactory';
import { UserAccount } from '../../common/UserAccount';
import { useUserState } from '../../services/user/UserContext';
import { UserService } from '../../services/user/UserService';
import { useUserService } from '../../services/user/UserServiceContext';
import { Page } from '../Page';
import { Alert } from '../../components/alerts/Alert';

import './MySingleMomentPage.scss';

import { MetadataService } from '../../services/metadata/MetadataService';
import { NFTCardList } from '../../components/nft-card/NFTCardList';
import { NFTCardSize } from '../../components/nft-card/NFTCard';
import { Moment } from '../../components/moment/Moment';

const log = debug('app:pages:profile:MySingleMomentPage');

type NFTPageProps = {
  userState: UserAccount,
  userService: UserService,
  metadataService: MetadataService,

  nftIdStr: string,
  profileId: string
}

type NFTPageState = {
  nftId: number,
  metadata: Play.NFTMetadata,
  loading: boolean,
  addingToMarket: boolean,
  error: string,
  redirectTo: string,
  price: string,
  priceError: string,

  otherNftMetadata: Play.NFTMetadata[],
  loadingOtherNfts: boolean
}

const priceRegex = /^\d+\.\d+$/;

class InternalNFTPage extends React.Component<NFTPageProps, NFTPageState> {
  private marketplace = getMarketplace();
  constructor(props: NFTPageProps) {
    super(props);

    this.state = {
      nftId: undefined,
      metadata: undefined,
      loading: false,
      addingToMarket: false,
      error: undefined,
      redirectTo: undefined,
      price: '',
      priceError: undefined,

      otherNftMetadata: [],
      loadingOtherNfts: false,
    };

    this.addToMarket = this.addToMarket.bind(this);
    this.onPriceChange = this.onPriceChange.bind(this);
  }

  componentDidMount() {
    const nftId = this.parseId();
    if (nftId !== undefined) {
      this.loadMetadata(nftId);
    }
    this.loadMultiple({ skip: nftId });
  }

  // TODO allow changing of ID

  private parseId(): number {
    if (!this.props.nftIdStr) {
      this.setState({ error: 'Missing ID.' });
      return;
    }
    try {
      const nftId = parseInt(this.props.nftIdStr);
      if (nftId !== this.state.nftId) {
        this.setState({ error: undefined, nftId });
      }
      return nftId;
    } catch (e) {
      log('error parsing ID', e);
      this.setState({ error: 'Invalid ID format.' });
      return undefined;
    }
  }

  private async loadMetadata(id?: number) {
    if (id === undefined) {
      id = this.state.nftId;
    }
    if (id === undefined) {
      return;
    }
    this.setState({ loading: true });

    try {
      const metadata = await this.props.userService.loadSingleMetadata(id);
      this.setState({ loading: false, metadata });
    } catch (e) {
      this.setState({ loading: false, error: e.message})
    }
  }

  private async loadMultiple(opts: { skip: number | undefined }) {
    this.setState({ loadingOtherNfts: true });

    try {
      const profileId = this.props.profileId === 'me' ?
        this.props.userState.address
        : this.props.profileId;
      const account = await this.props.userService.loadPublicAccount(profileId);
      const nftsToLoad = account?.nfts?.filter(x => x !== opts.skip);
      if (!nftsToLoad.length) {
        this.setState({ otherNftMetadata: [] });
        return;
      }
      const metadataMap = await this.props.metadataService.loadNFTMetadata(nftsToLoad, profileId);
      const otherNftMetadata = Object.values(metadataMap).filter(metadata => !!metadata);
      this.setState({ otherNftMetadata });
    }
    finally {
      this.setState({ loadingOtherNfts: false });
    }
  }

  private async addToMarket() {
    if (!priceRegex.test(this.state.price)) { return; }
    this.setState({ addingToMarket: true });
    try {
      await this.marketplace.addToMarket(this.state.nftId, this.state.price);
      this.setState({
        addingToMarket: false,
        redirectTo: '/profile',
        error: undefined
      });
    } catch (e) {
      this.setState({
        addingToMarket: false,
        error: e.message
      });
    }
  }

  private onPriceChange(e) {
    const price = e.target.value;
    const priceError = priceRegex.test(price) ? undefined : 'Invalid price format';
    this.setState({ price, priceError });
  }

  render() {
    const addingToMarket = this.state.addingToMarket;
    return (
      <Page className="my-single-moment-page container align-items-stretch py-2">
        {this.state.redirectTo && (
          <Navigate to={this.state.redirectTo} />
        )}
        {this.state.error && (
          <Alert.Danger>{this.state.error}</Alert.Danger>
        )}
        {this.state.loading ? (
          <Alert.Info>Loading...</Alert.Info>
        ) : this.state.metadata ? (
          <>
            <div className="mb-3">
              <label htmlFor="nft-price" className="form-label">Price</label>
              <input id="nft-price" className="form-control" value={this.state.price} onChange={this.onPriceChange} />
              {this.state.priceError && (
                <span>{this.state.priceError}</span>
              )}
            </div>
            <div className="mb-3">
              <button className="btn btn-success" disabled={
                addingToMarket || !!this.state.priceError ||
                !this.state.metadata || !!this.state.metadata.reservedFor
              } onClick={this.addToMarket}>
                Put up for sale
              </button>
              {addingToMarket && (
                <span>Adding to market...</span>
              )}
            </div>

            <Moment metadata={this.state.metadata} backText="Back to Collections" backURL={ `/profile/${this.props.profileId}/moments` } >
              <div>
                <button className="btn btn-primary disabled">EXPLORE MOMENTS</button>
              </div>
            </Moment>

          </>
        ) : (
          <Alert.Info>No metadata</Alert.Info>
        )}
        {this.state.loadingOtherNfts ? (
          <div className="alert alert-info">Loading more NFTs...</div>
        ) : (
          <NFTCardList nftItems={this.state.otherNftMetadata} cardSize={NFTCardSize.Small} baseUrl={`/profile/${this.props.profileId}/moments/`} />
        )}

      </Page>
    );
  }
}

export function MySingleMomentPage() {
  const userState = useUserState();
  const userService = useUserService();
  const metadataService = getMetadataService();

  const { profileId, nftId } = useParams();

  return (
    <InternalNFTPage
      userState={userState}
      userService={userService}
      metadataService={metadataService}
      nftIdStr={nftId}
      profileId={profileId}
    />
  )
}
